import { conceptsData } from '../data/concepts-data.js';
import { renderConceptFeedbackWidget, renderMicroReactions } from './inline-feedback.js';

export function renderConceptsView(activeConceptId = 'concept-1', activeSubTab = 'collateral') {
  const activeConcept = conceptsData.find(c => c.id === activeConceptId) || conceptsData[0];

  return `
    <section class="view-section" id="view-concepts">
      <!-- Top Hero Header -->
      <div class="hero-card" style="border-left: 6px solid ${activeConcept.colors[0].hex}; position: relative; overflow: hidden;">
        <div style="display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: var(--space-md);">
          <div>
            <span class="hero-tag" style="background: rgba(255,255,255,0.1); color: var(--text-primary);">
              Phase 04 Creative Direction • Concept ${activeConcept.number}
            </span>
            <h2 style="font-size: var(--text-4xl); margin-top: 8px; margin-bottom: 8px; color: var(--text-primary);">
              ${activeConcept.name}
            </h2>
            <p style="max-width: 780px; font-size: var(--text-lg); margin-bottom: 0; color: var(--text-secondary);">
              <em>"${activeConcept.tagline}"</em> — ${activeConcept.summary}
            </p>
          </div>

          <!-- Live Theme Skinning Button -->
          <div style="display: flex; flex-direction: column; gap: var(--space-xs); align-items: flex-end;">
            <button 
              class="btn btn-secondary" 
              id="btn-apply-theme" 
              data-concept-theme="${activeConcept.id}"
              style="border-color: ${activeConcept.colors[0].hex}; color: var(--text-primary); font-size: var(--text-xs); padding: 8px 16px;"
              title="Apply this concept's color tokens and font hierarchy across the interface"
            >
              <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: ${activeConcept.colors[0].hex}; margin-right: 6px;"></span>
              Apply ${activeConcept.name} Theme to UI
            </button>
            <span class="mono" style="font-size: 10px; color: var(--text-tertiary);">Live CSS Token Switcher</span>
          </div>
        </div>
      </div>

      <!-- Main Concept Selector Pill Bar -->
      <div class="concept-selector-bar" role="tablist" style="margin-bottom: var(--space-lg);">
        ${conceptsData.map(c => `
          <button 
            class="concept-pill-btn ${c.id === activeConcept.id ? 'active' : ''}" 
            data-concept="${c.id}"
            id="tab-${c.id}"
            role="tab"
            aria-selected="${c.id === activeConcept.id}"
            style="${c.id === activeConcept.id ? `border-bottom: 3px solid ${c.colors[0].hex};` : ''}"
          >
            <span class="mono" style="opacity: 0.7; margin-right: 4px;">0${c.number}</span>
            ${c.name}
          </button>
        `).join('')}
      </div>

      <!-- Concept Sub-Navigation Tabs -->
      <div class="sub-nav-bar" style="display: flex; gap: var(--space-sm); border-bottom: 1px solid var(--border-subtle); padding-bottom: var(--space-sm); margin-bottom: var(--space-xl); overflow-x: auto;">
        <button class="sub-tab-btn ${activeSubTab === 'collateral' ? 'active' : ''}" data-subtab="collateral">
          🖼️ Real-World Collateral
        </button>
        <button class="sub-tab-btn ${activeSubTab === 'visual' ? 'active' : ''}" data-subtab="visual">
          🎨 Vector Logo &amp; Multi-Site
        </button>
        <button class="sub-tab-btn ${activeSubTab === 'tokens' ? 'active' : ''}" data-subtab="tokens">
          ♿ Color Tokens &amp; WCAG
        </button>
        <button class="sub-tab-btn ${activeSubTab === 'typography' ? 'active' : ''}" data-subtab="typography">
          🔤 Typography &amp; Hierarchy
        </button>
        <button class="sub-tab-btn ${activeSubTab === 'messaging' ? 'active' : ''}" data-subtab="messaging">
          ✍️ Copy &amp; Sermon Campaigns
        </button>
        <button class="sub-tab-btn ${activeSubTab === 'strategy' ? 'active' : ''}" data-subtab="strategy">
          🧭 Strategic Alignment
        </button>
      </div>

      <!-- Sub-Tab Content Area -->
      <div class="subtab-content-container">
        ${renderSubTabContent(activeConcept, activeSubTab)}
      </div>

      <!-- Inline Review & Rubric Evaluation Widget -->
      ${renderConceptFeedbackWidget(activeConcept)}

      <!-- Image Lightbox Modal Container -->
      <div id="mockup-lightbox-modal" class="lightbox-modal" style="display: none;">
        <div class="lightbox-overlay" id="lightbox-backdrop"></div>
        <div class="lightbox-content-box">
          <button class="lightbox-close-btn" id="lightbox-close">&times;</button>
          <img id="lightbox-img" src="" alt="Mockup preview" style="max-width: 100%; max-height: 80vh; border-radius: var(--radius-md); object-fit: contain;" />
          <div style="margin-top: var(--space-md); text-align: center;">
            <h4 id="lightbox-title" style="color: #FFFFFF; margin-bottom: 4px;"></h4>
            <p id="lightbox-desc" style="color: #A3A8C2; font-size: var(--text-sm); margin-bottom: 0;"></p>
          </div>
        </div>
      </div>
    </section>
  `;
}

function renderSubTabContent(concept, subTab) {
  switch (subTab) {
    case 'collateral':
      return renderCollateralGallery(concept);
    case 'visual':
      return renderVisualLogoInspector(concept);
    case 'tokens':
      return renderColorTokensAndWCAG(concept);
    case 'typography':
      return renderTypographyPlayground(concept);
    case 'messaging':
      return renderMessagingPlaybook(concept);
    case 'strategy':
      return renderStrategyAlignment(concept);
    default:
      return renderCollateralGallery(concept);
  }
}

/* Sub-Tab 1: Real-World Collateral Gallery */
function renderCollateralGallery(concept) {
  return `
    <div style="display: flex; flex-direction: column; gap: var(--space-xl);">
      <div style="display: flex; justify-content: space-between; align-items: flex-end; flex-wrap: wrap; gap: var(--space-md);">
        <div>
          <h3 style="font-size: var(--text-2xl); color: var(--text-primary); margin-bottom: 4px;">
            Real-World Collateral Suite
          </h3>
          <p style="font-size: var(--text-sm); color: var(--text-secondary); margin-bottom: 0;">
            Agency-grade visual touchpoints generated for <strong>Concept ${concept.number}: ${concept.name}</strong>. Click any image to view in high-resolution lightbox.
          </p>
        </div>
        <span class="mono" style="font-size: var(--text-xs); color: var(--accent-primary); background: var(--bg-surface-elevated); padding: 6px 12px; border-radius: var(--radius-sm); border: 1px solid var(--border-subtle);">
          4 Touchpoints Generated
        </span>
      </div>

      <div class="grid-2" style="gap: var(--space-xl);">
        ${concept.mockups.map(mockup => `
          <div class="card mockup-card" style="padding: 0; overflow: hidden; display: flex; flex-direction: column; cursor: pointer; transition: transform 0.25s ease, box-shadow 0.25s ease;" data-lightbox-src="${mockup.image}" data-lightbox-title="${mockup.title}" data-lightbox-desc="${mockup.description}">
            <div style="position: relative; overflow: hidden; background: #0F1117; aspect-ratio: 4/3;">
              <img 
                src="${mockup.image}" 
                alt="${mockup.title}" 
                style="width: 100%; height: 100%; object-fit: cover; transition: transform 0.4s ease;" 
                class="mockup-img"
                loading="lazy"
              />
              <div style="position: absolute; top: var(--space-sm); left: var(--space-sm); background: rgba(15, 17, 23, 0.85); backdrop-filter: blur(8px); padding: 4px 10px; border-radius: var(--radius-sm); font-size: 11px; color: #FFFFFF; font-family: var(--font-mono); border: 1px solid rgba(255,255,255,0.15);">
                ${mockup.title}
              </div>
              <div class="mockup-overlay-hint" style="position: absolute; bottom: 0; left: 0; right: 0; padding: var(--space-sm) var(--space-md); background: linear-gradient(to top, rgba(0,0,0,0.9), transparent); color: #FFFFFF; font-size: 12px; display: flex; justify-content: space-between; align-items: center; opacity: 0; transition: opacity 0.25s ease;">
                <span>🔍 Click to Expand</span>
                <span class="mono" style="font-size: 10px; opacity: 0.8;">HD PREVIEW</span>
              </div>
            </div>
            <div style="padding: var(--space-lg); flex-grow: 1; display: flex; flex-direction: column; justify-content: space-between;">
              <div>
                <div style="font-size: var(--text-xs); color: ${concept.colors[0].hex}; font-weight: 700; text-transform: uppercase; margin-bottom: 4px;">
                  ${mockup.subtitle}
                </div>
                <h4 style="font-size: var(--text-lg); color: var(--text-primary); margin-bottom: var(--space-xs);">
                  ${mockup.title}
                </h4>
                <p style="font-size: var(--text-sm); color: var(--text-secondary); line-height: 1.6; margin-bottom: var(--space-sm);">
                  ${mockup.description}
                </p>
              </div>
              <div style="border-top: 1px solid var(--border-subtle); padding-top: var(--space-xs); margin-top: var(--space-xs);">
                ${renderMicroReactions(concept.id, mockup.id)}
              </div>
            </div>
          </div>
        `).join('')}
      </div>
    </div>
  `;
}

/* Sub-Tab 2: Vector Logo & Multi-Site Inspector */
function renderVisualLogoInspector(concept) {
  return `
    <div style="display: flex; flex-direction: column; gap: var(--space-xl);">
      <div class="card">
        <div class="card-header">
          <div>
            <h3 style="font-size: var(--text-xl); color: var(--text-primary);">Vector Logo Mark &amp; Architecture</h3>
            <p style="font-size: var(--text-sm); color: var(--text-secondary); margin-bottom: 0;">
              Archetype: <strong>${concept.archetype}</strong>
            </p>
          </div>
          <div style="display: flex; gap: var(--space-sm);">
            <button class="btn btn-secondary btn-sm logo-bg-toggle" data-bg="#0F1117">Dark Void</button>
            <button class="btn btn-secondary btn-sm logo-bg-toggle" data-bg="#FBF8F2">Warm Linen</button>
            <button class="btn btn-secondary btn-sm logo-bg-toggle" data-bg="#0C162C">Sanctuary Navy</button>
          </div>
        </div>

        <div class="grid-2" style="gap: var(--space-xl); align-items: center;">
          <!-- SVG Mark Box -->
          <div id="logo-preview-canvas" style="background: #0F1117; border-radius: var(--radius-lg); padding: var(--space-2xl); display: flex; justify-content: center; align-items: center; min-height: 280px; border: 1px solid var(--border-subtle); transition: background 0.3s ease;">
            <img id="active-logo-img" src="${concept.logos.mark}" alt="${concept.name} Logo Mark" style="max-width: 200px; max-height: 200px; width: 100%; filter: drop-shadow(0 10px 20px rgba(0,0,0,0.3));" />
          </div>

          <!-- Specs and Geometry -->
          <div style="display: flex; flex-direction: column; gap: var(--space-md);">
            <div style="background: var(--bg-surface-elevated); padding: var(--space-md); border-radius: var(--radius-md); border: 1px solid var(--border-subtle);">
              <span class="mono" style="font-size: 11px; color: ${concept.colors[0].hex}; font-weight: 700;">PRIMARY MARK</span>
              <h4 style="font-size: var(--text-base); margin-top: 4px; color: var(--text-primary);">${concept.name} Crest</h4>
              <p style="font-size: var(--text-xs); color: var(--text-secondary); margin-bottom: 0;">
                Calculated geometric vector with minimum clearspace rule of 0.5X and high-legibility micro-rendering down to 24px.
              </p>
            </div>

            <div style="background: var(--bg-surface-elevated); padding: var(--space-md); border-radius: var(--radius-md); border: 1px solid var(--border-subtle);">
              <span class="mono" style="font-size: 11px; color: ${concept.colors[0].hex}; font-weight: 700;">HORIZONTAL WORDMARK LOCKUP</span>
              <div style="margin-top: 8px; background: rgba(0,0,0,0.2); padding: 12px; border-radius: var(--radius-sm);">
                <img src="${concept.logos.horizontal}" alt="${concept.name} Horizontal Lockup" style="max-width: 100%; height: auto;" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Multi-Site Neighborhood Sub-Branding -->
      <div class="card">
        <div class="card-header">
          <div>
            <h3 style="font-size: var(--text-xl); color: var(--text-primary);">Multi-Site Sub-Branding System</h3>
            <p style="font-size: var(--text-sm); color: var(--text-secondary); margin-bottom: 0;">
              Interchangeable neighborhood lockup formulas for Chicago campuses.
            </p>
          </div>
          <span class="hero-tag" style="background: rgba(255,255,255,0.06); color: var(--text-primary);">Modular Masterbrand</span>
        </div>

        <div class="grid-3" style="gap: var(--space-md);">
          ${concept.logos.subSites.map(site => `
            <div style="background: var(--bg-surface-elevated); border-radius: var(--radius-md); padding: var(--space-lg); border-top: 3px solid ${site.color}; display: flex; flex-direction: column; justify-content: space-between; gap: var(--space-md);">
              <div>
                <div class="mono" style="font-size: 10px; color: ${site.color}; font-weight: 700; margin-bottom: 4px;">
                  SITE LOCKUP • ${site.name.toUpperCase()}
                </div>
                <div style="font-family: var(--font-mono); font-size: var(--text-sm); font-weight: 700; color: var(--text-primary); letter-spacing: 0.5px;">
                  ${site.label}
                </div>
              </div>
              <div style="display: flex; justify-content: space-between; align-items: center; border-top: 1px solid var(--border-subtle); padding-top: var(--space-sm);">
                <span class="mono" style="font-size: 10px; color: var(--text-tertiary);">${site.tag}</span>
                <span style="display: inline-block; width: 12px; height: 12px; border-radius: 50%; background: ${site.color};"></span>
              </div>
            </div>
          `).join('')}
        </div>
      </div>
    </div>
  `;
}

/* Sub-Tab 3: Color Tokens & WCAG Contrast Sandbox */
function renderColorTokensAndWCAG(concept) {
  return `
    <div style="display: flex; flex-direction: column; gap: var(--space-xl);">
      <!-- Swatch Matrix -->
      <div class="card">
        <div class="card-header">
          <div>
            <h3 style="font-size: var(--text-xl); color: var(--text-primary);">Semantic Color System</h3>
            <p style="font-size: var(--text-sm); color: var(--text-secondary); margin-bottom: 0;">
              Tokens formulated for high emotional distinction and strict accessibility thresholds.
            </p>
          </div>
          <span class="mono" style="font-size: var(--text-xs); color: var(--text-tertiary);">HEX / RGB / HSL</span>
        </div>

        <div class="grid-3" style="gap: var(--space-md);">
          ${concept.colors.map(color => `
            <div style="background: var(--bg-surface-elevated); border-radius: var(--radius-md); overflow: hidden; border: 1px solid var(--border-subtle);">
              <div style="background-color: ${color.hex}; height: 80px; display: flex; align-items: flex-end; padding: var(--space-sm);">
                <span class="mono" style="font-size: 11px; font-weight: 700; background: rgba(0,0,0,0.6); color: #FFFFFF; padding: 2px 6px; border-radius: 4px;">
                  ${color.hex}
                </span>
              </div>
              <div style="padding: var(--space-md);">
                <h4 style="font-size: var(--text-base); color: var(--text-primary); margin-bottom: 2px;">${color.name}</h4>
                <div style="font-size: var(--text-xs); color: ${concept.colors[0].hex}; font-weight: 600; margin-bottom: 8px;">${color.role}</div>
                <div class="mono" style="font-size: 10px; color: var(--text-tertiary); display: flex; flex-direction: column; gap: 2px;">
                  <span>${color.rgb}</span>
                  <span>${color.hsl}</span>
                </div>
              </div>
            </div>
          `).join('')}
        </div>
      </div>

      <!-- Real-Time Interactive WCAG Contrast Testing Sandbox -->
      <div class="card" style="border: 2px solid var(--border-subtle);">
        <div class="card-header">
          <div>
            <h3 style="font-size: var(--text-xl); color: var(--text-primary);">Interactive WCAG 2.1 Contrast Testing Sandbox</h3>
            <p style="font-size: var(--text-sm); color: var(--text-secondary); margin-bottom: 0;">
              Test live color combinations against W3C WCAG 2.1 AA/AAA accessibility standards.
            </p>
          </div>
          <span class="hero-tag" style="background: #00C853; color: #000000; font-weight: 700;">LIVE CALCULATOR</span>
        </div>

        <div class="grid-2" style="gap: var(--space-xl); align-items: stretch;">
          <!-- Controls -->
          <div style="display: flex; flex-direction: column; gap: var(--space-lg);">
            <div>
              <label style="display: block; font-size: var(--text-xs); font-weight: 700; color: var(--text-secondary); text-transform: uppercase; margin-bottom: var(--space-xs);">
                Select Foreground (Text) Color:
              </label>
              <select id="wcag-fg-select" class="form-select" style="width: 100%; background: var(--bg-surface-elevated); color: var(--text-primary); padding: 10px; border-radius: var(--radius-md); border: 1px solid var(--border-subtle);">
                ${concept.colors.map((c, i) => `
                  <option value="${c.hex}" ${i === 0 ? 'selected' : ''}>${c.name} (${c.hex})</option>
                `).join('')}
                <option value="#FFFFFF">Pure White (#FFFFFF)</option>
                <option value="#0F1117">Void Obsidian (#0F1117)</option>
              </select>
            </div>

            <div>
              <label style="display: block; font-size: var(--text-xs); font-weight: 700; color: var(--text-secondary); text-transform: uppercase; margin-bottom: var(--space-xs);">
                Select Background Surface Color:
              </label>
              <select id="wcag-bg-select" class="form-select" style="width: 100%; background: var(--bg-surface-elevated); color: var(--text-primary); padding: 10px; border-radius: var(--radius-md); border: 1px solid var(--border-subtle);">
                <option value="#0F1117" selected>Void Obsidian (#0F1117)</option>
                <option value="#F8F9FE">Clean White Tint (#F8F9FE)</option>
                <option value="#FBF8F2">Warm Linen (#FBF8F2)</option>
                <option value="#0C162C">Sanctuary Navy (#0C162C)</option>
                ${concept.colors.map(c => `
                  <option value="${c.hex}">${c.name} (${c.hex})</option>
                `).join('')}
              </select>
            </div>

            <div style="display: flex; gap: var(--space-sm); flex-wrap: wrap;">
              <button class="btn btn-secondary btn-sm wcag-preset-btn" data-fg="#FFFFFF" data-bg="${concept.colors[0].hex}">Preset: White on Primary</button>
              <button class="btn btn-secondary btn-sm wcag-preset-btn" data-fg="${concept.colors[1].hex}" data-bg="#0F1117">Preset: Accent on Dark</button>
              <button class="btn btn-secondary btn-sm wcag-preset-btn" data-fg="${concept.colors[0].hex}" data-bg="#F8F9FE">Preset: Primary on Light</button>
            </div>
          </div>

          <!-- Live Result Display Box -->
          <div id="wcag-result-preview" style="background-color: #0F1117; color: ${concept.colors[0].hex}; border-radius: var(--radius-lg); padding: var(--space-xl); display: flex; flex-direction: column; justify-content: space-between; border: 1px solid var(--border-subtle); min-height: 220px; transition: all 0.25s ease;">
            <div>
              <div style="font-size: var(--text-2xl); font-weight: 700; margin-bottom: 4px;" id="wcag-preview-headline">
                Bold. Inclusive. Relevant.
              </div>
              <p style="font-size: var(--text-sm); line-height: 1.5; margin-bottom: 0; opacity: 0.9;" id="wcag-preview-body">
                Urban Village Church creates Jesus-loving, inclusive communities that ignite the city and beyond.
              </p>
            </div>

            <div style="display: flex; justify-content: space-between; align-items: center; border-top: 1px solid rgba(255,255,255,0.2); padding-top: var(--space-md); margin-top: var(--space-md); flex-wrap: wrap; gap: var(--space-sm);">
              <div>
                <span class="mono" style="font-size: 11px; opacity: 0.8;">CONTRAST RATIO:</span>
                <span id="wcag-ratio-number" class="mono" style="font-size: var(--text-2xl); font-weight: 800; margin-left: 6px;">8.7 : 1</span>
              </div>
              <div style="display: flex; gap: var(--space-xs);" id="wcag-badges-box">
                <span class="badge badge-success" style="background: #00C853; color: #000000; font-weight: 700;">AAA Normal Pass</span>
                <span class="badge badge-success" style="background: #00C853; color: #000000; font-weight: 700;">AAA Large Pass</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

/* Sub-Tab 4: Typography & Hierarchy Playground */
function renderTypographyPlayground(concept) {
  return `
    <div style="display: flex; flex-direction: column; gap: var(--space-xl);">
      <div class="grid-2">
        <div class="card">
          <h3 style="font-size: var(--text-xl); color: var(--text-primary); margin-bottom: var(--space-md);">
            Typeface Stack &amp; Pairing Spec
          </h3>
          <div style="display: flex; flex-direction: column; gap: var(--space-md);">
            <div style="background: var(--bg-surface-elevated); padding: var(--space-lg); border-radius: var(--radius-md); border-left: 4px solid ${concept.colors[0].hex};">
              <span class="mono" style="font-size: 11px; color: ${concept.colors[0].hex}; font-weight: 700;">DISPLAY HEADLINE TYPEFACE</span>
              <div style="font-size: var(--text-2xl); font-weight: 700; color: var(--text-primary); margin-top: 4px;">
                ${concept.fonts.display}
              </div>
              <p style="font-size: var(--text-xs); color: var(--text-secondary); margin-top: 6px; margin-bottom: 0;">
                High emotional valence and distinctive character for street banners, campaign series, and stage titles.
              </p>
            </div>

            <div style="background: var(--bg-surface-elevated); padding: var(--space-lg); border-radius: var(--radius-md); border-left: 4px solid ${concept.colors[1].hex};">
              <span class="mono" style="font-size: 11px; color: ${concept.colors[1].hex}; font-weight: 700;">BODY &amp; READING TYPEFACE</span>
              <div style="font-size: var(--text-lg); font-weight: 500; color: var(--text-primary); margin-top: 4px;">
                ${concept.fonts.body}
              </div>
              <p style="font-size: var(--text-xs); color: var(--text-secondary); margin-top: 6px; margin-bottom: 0;">
                Optimized for extended reading legibility across mobile liturgies, email newsletters, and long-form statements.
              </p>
            </div>

            <div style="background: var(--bg-surface-elevated); padding: var(--space-lg); border-radius: var(--radius-md); border-left: 4px solid ${concept.colors[2] ? concept.colors[2].hex : '#999'};">
              <span class="mono" style="font-size: 11px; color: var(--accent-primary); font-weight: 700;">ACCENT &amp; METADATA TYPEFACE</span>
              <div class="mono" style="font-size: var(--text-base); font-weight: 600; color: var(--text-primary); margin-top: 4px;">
                ${concept.fonts.mono}
              </div>
              <p style="font-size: var(--text-xs); color: var(--text-secondary); margin-top: 6px; margin-bottom: 0;">
                Used for neighborhood coordinates, scripture references, date stamps, and navigational badges.
              </p>
            </div>
          </div>
        </div>

        <!-- Interactive Specimen Editor -->
        <div class="card">
          <div class="card-header">
            <h3 style="font-size: var(--text-xl); color: var(--text-primary);">Interactive Hierarchy Specimen</h3>
            <span class="mono" style="font-size: 11px; color: var(--text-tertiary);">Editable Canvas</span>
          </div>

          <div style="display: flex; flex-direction: column; gap: var(--space-md);">
            <div style="border-bottom: 1px solid var(--border-subtle); padding-bottom: var(--space-md);">
              <span class="mono" style="font-size: 10px; color: var(--text-tertiary);">DISPLAY LARGE (56PX)</span>
              <div contenteditable="true" style="font-size: 38px; font-weight: 800; line-height: 1.15; color: var(--text-primary); outline: none; margin-top: 4px;" title="Click to edit text">
                Radical Love in the Windy City.
              </div>
            </div>

            <div style="border-bottom: 1px solid var(--border-subtle); padding-bottom: var(--space-md);">
              <span class="mono" style="font-size: 10px; color: var(--text-tertiary);">HEADLINE 1 (28PX)</span>
              <div contenteditable="true" style="font-size: 24px; font-weight: 700; line-height: 1.25; color: ${concept.colors[0].hex}; outline: none; margin-top: 4px;" title="Click to edit text">
                ${concept.tagline}
              </div>
            </div>

            <div>
              <span class="mono" style="font-size: 10px; color: var(--text-tertiary);">BODY COPY (16PX)</span>
              <div contenteditable="true" style="font-size: 15px; line-height: 1.7; color: var(--text-secondary); outline: none; margin-top: 4px;" title="Click to edit text">
                ${concept.samplePostCopy}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

/* Sub-Tab 5: Copy & Campaign Messaging Playbook */
function renderMessagingPlaybook(concept) {
  return `
    <div style="display: flex; flex-direction: column; gap: var(--space-xl);">
      <!-- Slogans & Voice Posture -->
      <div class="grid-2">
        <div class="card">
          <h3 style="font-size: var(--text-xl); color: var(--text-primary); margin-bottom: var(--space-md);">
            Anchor Slogans &amp; Tagline Suite
          </h3>
          <div style="display: flex; flex-direction: column; gap: var(--space-sm);">
            <div style="background: var(--bg-surface-elevated); padding: var(--space-md); border-radius: var(--radius-md); border-left: 3px solid ${concept.colors[0].hex};">
              <span class="mono" style="font-size: 10px; color: ${concept.colors[0].hex}; font-weight: 700;">PRIMARY ANCHOR</span>
              <div style="font-size: var(--text-lg); font-weight: 700; color: var(--text-primary); margin-top: 2px;">
                "${concept.tagline}"
              </div>
            </div>
            ${concept.secondaryTaglines.map(tag => `
              <div style="background: var(--bg-surface-elevated); padding: var(--space-sm) var(--space-md); border-radius: var(--radius-sm); border: 1px solid var(--border-subtle); font-size: var(--text-sm); color: var(--text-primary);">
                "${tag}"
              </div>
            `).join('')}
          </div>
        </div>

        <div class="card">
          <h3 style="font-size: var(--text-xl); color: var(--text-primary); margin-bottom: var(--space-md);">
            Sidewalk A-Frame &amp; Street Invitation
          </h3>
          <div style="background: #0F1117; border-radius: var(--radius-md); padding: var(--space-xl); border: 2px solid ${concept.colors[0].hex};">
            <div class="mono" style="font-size: 11px; color: ${concept.colors[1] ? concept.colors[1].hex : '#FFB300'}; margin-bottom: var(--space-sm); font-weight: 700;">
              SIDEWALK BOARD COPY SPECIMEN
            </div>
            <div style="font-size: var(--text-lg); font-weight: 700; color: #FFFFFF; line-height: 1.4; margin-bottom: var(--space-md);">
              ${concept.sidewalkQuote}
            </div>
            <div class="mono" style="font-size: 10px; color: #A3A8C2;">
              LOCATION: HPW • RAVENSWOOD • WICKER PARK • RIVER FOREST • ONLINE
            </div>
          </div>
        </div>
      </div>

      <!-- Sermon Series Packages -->
      <div class="card">
        <div class="card-header">
          <div>
            <h3 style="font-size: var(--text-xl); color: var(--text-primary);">Featured Sermon Series Campaign Packages</h3>
            <p style="font-size: var(--text-sm); color: var(--text-secondary); margin-bottom: 0;">
              Complete 4-week thematic narrative architecture tailored to this concept.
            </p>
          </div>
          <span class="hero-tag" style="background: rgba(255,255,255,0.08); color: var(--text-primary);">Liturgy &amp; Preaching</span>
        </div>

        <div class="grid-2" style="gap: var(--space-lg);">
          ${concept.sermonSeries.map(series => `
            <div style="background: var(--bg-surface-elevated); border-radius: var(--radius-md); padding: var(--space-lg); border: 1px solid var(--border-subtle); display: flex; flex-direction: column; justify-content: space-between;">
              <div>
                <span class="mono" style="font-size: 11px; color: ${concept.colors[0].hex}; font-weight: 700;">4-WEEK PREACHING SERIES</span>
                <h4 style="font-size: var(--text-xl); color: var(--text-primary); margin-top: 4px; margin-bottom: 4px;">${series.title}</h4>
                <div style="font-size: var(--text-xs); color: var(--text-tertiary); margin-bottom: var(--space-md);">
                  <em>"${series.tagline}"</em> • Anchor: ${series.scripture}
                </div>
                
                <div style="display: flex; flex-direction: column; gap: var(--space-xs);">
                  ${series.topics.map((topic, i) => `
                    <div style="display: flex; align-items: flex-start; gap: var(--space-sm); font-size: var(--text-sm); color: var(--text-secondary);">
                      <span class="mono" style="color: ${concept.colors[0].hex}; font-weight: 700; font-size: 11px;">W0${i+1}</span>
                      <span>${topic}</span>
                    </div>
                  `).join('')}
                </div>
              </div>
            </div>
          `).join('')}
        </div>
      </div>
    </div>
  `;
}

/* Sub-Tab 6: Strategic Alignment */
function renderStrategyAlignment(concept) {
  return `
    <div style="display: flex; flex-direction: column; gap: var(--space-xl);">
      <div class="grid-3">
        ${concept.strategicPillars.map((pillar, i) => `
          <div class="card" style="border-top: 4px solid ${concept.colors[i % concept.colors.length].hex};">
            <span class="mono" style="font-size: 11px; color: ${concept.colors[i % concept.colors.length].hex}; font-weight: 700;">
              STRATEGIC PILLAR 0${i+1}
            </span>
            <h4 style="font-size: var(--text-lg); color: var(--text-primary); margin-top: 4px; margin-bottom: var(--space-sm);">
              ${pillar.title}
            </h4>
            <p style="font-size: var(--text-sm); color: var(--text-secondary); line-height: 1.6; margin-bottom: 0;">
              ${pillar.desc}
            </p>
          </div>
        `).join('')}
      </div>

      <div class="card">
        <h3 style="font-size: var(--text-xl); color: var(--text-primary); margin-bottom: var(--space-sm);">
          Primary Audience Persona Alignment
        </h3>
        <p style="font-size: var(--text-base); color: var(--text-primary); line-height: 1.7; margin-bottom: var(--space-md);">
          <strong>Target Demographics:</strong> ${concept.audienceFit}
        </p>
        <div style="background: var(--bg-surface-elevated); padding: var(--space-lg); border-radius: var(--radius-md); border: 1px solid var(--border-subtle);">
          <div class="mono" style="font-size: 11px; color: ${concept.colors[0].hex}; font-weight: 700; margin-bottom: 4px;">
            EMOTIONAL VALUE PROPOSITION
          </div>
          <p style="font-size: var(--text-sm); color: var(--text-secondary); line-height: 1.6; margin-bottom: 0;">
            This concept delivers an experience characterized as <strong>${concept.emotionalTone}</strong>. It directly answers the deep cultural hunger identified in Phase 02 for authentic spiritual community without dogmatic gatekeeping.
          </p>
        </div>
      </div>
    </div>
  `;
}
